import { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import { Dropdown } from 'primereact/dropdown';
import { Button } from 'primereact/button';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { InputText } from 'primereact/inputtext';
import { MultiSelect } from 'primereact/multiselect';
import { TabView, TabPanel } from 'primereact/tabview';
import { Dialog } from 'primereact/dialog';
import { Checkbox } from 'primereact/checkbox';
import { RadioButton } from 'primereact/radiobutton';

import { RootState } from '../../../store';
import { alertService } from '../../../shared/services/alertService';
import { exportToExcel, ExcelColumn } from '../../../shared/export/excel';
import { exportToPdf, PdfColumn } from '../../../shared/export/pdf';
import {
  useGetStaffQuery,
  useGetPanelQuery,
  useLazyGetSubjectQuery,
  useLazyGetVacncyUpdateStsQuery,
  useLazyGetVacancyNeedListQuery,
  useAddVacancyNeedMutation,
  useLazyGetBulkSchFromUdiseQuery,
  useLazyGetVcntGoQuery,
  useVcntGoPostMutation,
  useVcntApprovalPostMutation,
  useLazyGetSignedUrlQuery,
  useUploadFileMutation,
  useLazyGetUploadedFilesQuery,
  useLazyGetBulkVacancyQuery,
  useGetMinorityTypeQuery,
} from '../counselling.services';
import {
  Option,
  ColumnDef,
  AIDED_COLUMNS,
  SCERT_COLUMNS,
  NORMAL_COLUMNS,
} from './types';
import {
  SUBJECT_DESIGNATIONS,
  subjectListKey,
} from '../../../shared/services/commonService';

/* eslint-disable @typescript-eslint/no-explicit-any */

/**
 * Migrated from Angular `VacancyUpdateComponent`
 * (src/app/views/pages/counselling/vacancy-update) — PHASES 1-2.
 *
 * Phase 1: Search (Designation / Panel / Subject) → single-update vacancy/need
 *          list with a column chooser, global search, total-vacancy footer and
 *          Excel export.
 * Phase 2: Detail form (school details + Vacancy/Need steppers) + save
 *          (addVacancyNeed) for normal / SCERT mode.
 * Phase 3: Bulk Excel upload tab (sample template, xlsx read, UDISE validation,
 *          getBulkSchFromUdise, 1000-row chunked addVacancyNeed).
 * Phase 4: Aided-schools mode (DDO code, minority, Eligible Vacant Post table
 *          with IFHRMS + GO file upload, declaration dialog, approval flow).
 * PDF export + bulk vacancy export land in Phase 5.
 */

const str = (v: any): string =>
  v === null || v === undefined ? '' : String(v);

/** Stable client-side row id for React keys (vacant-post rows). */
let uidSeq = 0;
const newUid = (): string => `row_${Date.now()}_${uidSeq++}`;

const VacancyUpdate = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const session = useSelector((state: RootState) => state.session);

  const userType = str(session.usertypeid);
  const userType1 = str(session.emisUsertype1);
  const districtId = Number(session.userId ?? 0);
  const districtName = str(session.districtName);

  // Handoff from DEE.clickStart (localStorage).
  const startPanelType = useMemo(
    () => localStorage.getItem('PanelType') ?? '',
    []
  );
  const startSubject = useMemo(() => localStorage.getItem('subject') ?? '', []);

  // ── Mode (Angular pathname + role) ─────────────────────────────────────────
  const isAidedSchls =
    location.pathname === '/counselling/vacancy-update-aided-sch';
  const isScert =
    (userType === '5' && userType1 === '6') ||
    (userType === '5' && userType1 === '7') ||
    userType === '22';

  // ── Static data ──────────────────────────────────────────────────────────
  const { data: staffRaw } = useGetStaffQuery();
  const { data: minorityTypeRaw } = useGetMinorityTypeQuery();
  const { data: panelRaw } = useGetPanelQuery();
  const [triggerSubject] = useLazyGetSubjectQuery();
  const [triggerVacUpdateSts] = useLazyGetVacncyUpdateStsQuery();
  const [triggerVacancyNeedList] = useLazyGetVacancyNeedListQuery();
  const [addVacancyNeed] = useAddVacancyNeedMutation();
  const [triggerBulkSchFromUdise] = useLazyGetBulkSchFromUdiseQuery();
  const [triggerVcntGo] = useLazyGetVcntGoQuery();
  const [vcntGoPost] = useVcntGoPostMutation();
  const [vcntApprovalPost] = useVcntApprovalPostMutation();
  const [triggerSignedUrl] = useLazyGetSignedUrlQuery();
  const [uploadFile] = useUploadFileMutation();
  const [triggerUploadedFiles] = useLazyGetUploadedFilesQuery();
  const [triggerBulkVacancy] = useLazyGetBulkVacancyQuery();

  // Designation list depends on mode/role.
  const staffList: Option[] = useMemo(() => {
    if (isAidedSchls) return staffRaw?.AidedStaffList;
    if (userType === '5' && userType1 === '8') return staffRaw?.KallarStaffList;
    if (isScert) return staffRaw?.SCERTStaffType ?? [];
    return staffRaw?.AllStaffType ?? [];
  }, [isAidedSchls, userType, userType1, isScert, staffRaw]);

  const panelList: Option[] = useMemo(() => {
    const list = panelRaw?.Panel ?? [];
    return list.map((mp: any) => ({ label: mp.label, value: mp.id }));
  }, [panelRaw]);

  // Minority type options (Angular minorityTypeList) — from MinorityType.json.
  const minorityTypeList: Option[] = useMemo(
    () => minorityTypeRaw?.MinorityTypeList ?? [],
    [minorityTypeRaw]
  );

  // Column set per mode.
  const allColumns: ColumnDef[] = useMemo(() => {
    if (isAidedSchls) return AIDED_COLUMNS;
    if (isScert) return SCERT_COLUMNS;
    return NORMAL_COLUMNS;
  }, [isAidedSchls, isScert]);

  // ── Search form state ──────────────────────────────────────────────────────
  const [designation, setDesignation] = useState('');
  const [panel, setPanel] = useState('');
  const [subject, setSubject] = useState('');
  const [subjectList, setSubjectList] = useState<Option[]>([]);
  const [isSubmitDisabled, setIsSubmitDisabled] = useState(true);

  // ── List state ─────────────────────────────────────────────────────────────
  const [schoolList, setSchoolList] = useState<any[]>([]);
  const [isListShow, setIsListShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [globalFilter, setGlobalFilter] = useState('');
  const [first, setFirst] = useState(0); // paginator offset for S.No column

  // ── Detail-screen state (Phase 2) ───────────────────────────────────────────
  const [isDetailsShow, setIsDetailsShow] = useState(false);
  const [udiseDetails, setUdiseDetails] = useState<any>(null);

  // ── Bulk upload state (Phase 3) ─────────────────────────────────────────────
  const [activeIndexId, setActiveIndexId] = useState(0);
  const [uploadData, setUploadData] = useState<any[]>([]);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // ── Aided-schools state (Phase 4) ───────────────────────────────────────────
  const [vcntArray, setVcntArray] = useState<any[]>([]);
  const [deleteArray, setDeleteArray] = useState<any[]>([]);
  const [declarationModel, setDeclarationModel] = useState(false);
  const [checkboxDclrtn, setCheckboxDclrtn] = useState(false);
  const [pstVancyNdParams, setPstVancyNdParams] = useState<any>(null);
  const [postVancyGoAry, setPostVancyGoAry] = useState<any[]>([]);

  // ── Bulk vacancy export state (Phase 5) ─────────────────────────────────────
  const [btnExcelLdg, setBtnExcelLdg] = useState(false);
  const [btnPdfLdg, setBtnPdfLdg] = useState(false);

  // Submit-forward label (Angular sbmtFwdText).
  const sbmtFwdText =
    userType === '30'
      ? 'Submit & Forward to CEO'
      : userType === '9' && isAidedSchls
        ? 'Submit & Approve'
        : 'Submit';

  // Column chooser — order preserved (Angular selectedCol getter/setter).
  const [selectedCols, setSelectedCols] = useState<ColumnDef[]>(allColumns);
  useEffect(() => setSelectedCols(allColumns), [allColumns]);
  const orderedCols = useMemo(
    () => allColumns.filter((c) => selectedCols.includes(c)),
    [allColumns, selectedCols]
  );

  // Prefill from DEE handoff.
  useEffect(() => {
    if (startPanelType) setPanel(startPanelType);
    if (startSubject) setSubject(startSubject);
  }, [startPanelType, startSubject]);

  // ── Subject loading per designation (Angular isSubFunc) ─────────────────────
  const loadSubjects = useCallback(
    async (desig: string) => {
      setPanel('');
      setSubject('');
      setIsSubmitDisabled(false);
      // Only designations that require a subject load a list.
      if (!SUBJECT_DESIGNATIONS.includes(desig)) {
        setSubjectList([]);
        return;
      }
      const key = subjectListKey(desig);
      try {
        const data: any = await triggerSubject().unwrap();
        setSubjectList(data?.[key] ?? data?.Subject ?? []);
      } catch {
        setSubjectList([]);
      }
    },
    [triggerSubject]
  );

  // Hide the result list + any open detail page when a filter changes, so a
  // stale table isn't shown until the user re-runs Submit.
  const hideList = useCallback(() => {
    setSchoolList([]);
    setIsListShow(false);
    setIsDetailsShow(false);
    setUdiseDetails(null);
    setFirst(0);
  }, []);

  // ── handleDesignation (Angular: GetVacncyUpdateSts gate for non-type-5) ─────
  const handleDesignation = useCallback(
    async (value: string) => {
      setDesignation(value);
      hideList();
      if (userType !== '5') {
        try {
          const res: any = await triggerVacUpdateSts({
            designation: value,
          }).unwrap();
          if (res?.dataStatus) {
            loadSubjects(value);
          } else {
            setSubjectList([]);
            setSubject('');
            setSchoolList([]);
            setIsSubmitDisabled(true);
            setPanel('');
            alertService.warning('Vacancy update stopped for this designation');
          }
        } catch {
          setIsSubmitDisabled(true);
        }
      } else {
        loadSubjects(value);
      }
    },
    [userType, triggerVacUpdateSts, loadSubjects, hideList]
  );

  // ── handleSubmit (Angular) → getVacancyNeedList ─────────────────────────────
  const totalVacancy = useMemo(
    () =>
      schoolList.reduce(
        (acc: number, cur: any) => acc + Number(cur.Vacancy || 0),
        0
      ),
    [schoolList]
  );

  const handleSubmit = useCallback(async () => {
    setSchoolList([]);
    if (!designation || !panel) {
      alertService.error('Please selecct counselling type and panel');
      return;
    }
    if (!(subjectList.length > 0 ? subject : true)) {
      alertService.error('Please select subject');
      return;
    }
    // Re-running the search returns to the list — hide any open detail page.
    setIsDetailsShow(false);
    setUdiseDetails(null);
    setLoading(true);
    setIsListShow(true);
    const flg = isAidedSchls ? '1' : '';
    try {
      const data: any = await triggerVacancyNeedList({
        udiseCode: '',
        designation,
        subject: subject ? subject : '',
        panel,
        flag: flg,
      }).unwrap();
      if (data?.dataStatus) {
        const rows = (data.result ?? []).map((row: any) => ({
          ...row,
          Vacancy: row.Vacancy ? row.Vacancy : 0,
          Need: row.Need ? row.Need : 0,
          btnText:
            userType === '30' &&
            (str(row.ApprovalSts) === '1' || str(row.ApprovalSts) === '2')
              ? 'View'
              : 'Edit',
        }));
        setSchoolList(rows);
      } else {
        setSchoolList([]);
        alertService.error(data.message);
      }
    } catch {
      setSchoolList([]);
    } finally {
      setLoading(false);
    }
  }, [
    designation,
    panel,
    subject,
    subjectList,
    isAidedSchls,
    userType,
    triggerVacancyNeedList,
  ]);

  // ── Aided: fetch GO signed URL for a vcntArray row (Angular getUploadedFiles) ─
  const fetchGoUrl = useCallback(
    async (filename: string, index: number) => {
      try {
        const result: any = await triggerUploadedFiles({
          bucketName: 'renewalapplicationemis',
          fileName: filename,
          expiry: 1800,
        }).unwrap();
        if (result?.url) {
          setVcntArray((prev) =>
            prev.map((r, i) =>
              i === index ? { ...r, postGoUrl: result.url } : r
            )
          );
        }
      } catch {
        /* ignore */
      }
    },
    [triggerUploadedFiles]
  );

  // ── Aided: load Eligible Vacant Post rows (Angular getVcntGo) ────────────────
  const getVcntGo = useCallback(
    async (row: any) => {
      try {
        const res: any = await triggerVcntGo({ vacancyId: row.VacId }).unwrap();
        let arr: any[] = res?.dataStatus ? (res.result ?? []) : [];
        arr = arr.map((r: any) => ({ ...r, postGoUrl: '', _uid: newUid() }));

        // Pad the array to match the Vacancy count.
        const vac = Number(row.Vacancy);
        while (arr.length < vac) {
          arr.push({
            ifhrmsId: '',
            postGoKey: '',
            postGoUrl: '',
            _uid: newUid(),
          });
        }
        setVcntArray(arr);
        setDeleteArray([]);

        // Resolve signed URLs for any existing GO files.
        arr.forEach((r, i) => {
          if (r.postGoKey) fetchGoUrl(r.postGoKey, i);
        });
      } catch {
        setVcntArray([]);
      }
    },
    [triggerVcntGo, fetchGoUrl]
  );

  // ── Edit → open detail form (Angular handleEdit) ────────────────────────────
  const handleEdit = useCallback(
    (row: any) => {
      const desg = staffList.filter((ft) => str(ft.value) === str(designation));

      // Aided: resolve minority type label → value.
      let minorityType = row.minorityType;
      if (isAidedSchls) {
        const mt = minorityTypeList.filter((m) => m.label === row.minorityType);
        minorityType = mt.length > 0 ? mt[0].value : row.minorityType;
      }
      setUdiseDetails({
        ...row,
        DesignationName: desg.length > 0 ? desg[0].label : '',
        Designation: designation,
        Vacancy: row.Vacancy ? row.Vacancy : 0,
        Need: row.Need ? row.Need : 0,
        ddoCode: row.ddoCode ? row.ddoCode : '',
        minorityYn: row.minorityYn,
        minorityType,
      });
      setIsDetailsShow(true);
      setIsListShow(false);

      if (isAidedSchls) {
        setVcntArray([]);
        setDeleteArray([]);
        getVcntGo(row);
      }
    },
    [staffList, designation, isAidedSchls, getVcntGo, minorityTypeList]
  );

  // ── Vacancy / Need steppers (capped at 50, Angular handleVacancy/Need) ──────
  const stepVacancy = useCallback(
    (delta: number) => {
      setUdiseDetails((prev: any) => {
        if (!prev) return prev;
        if (delta > 0 && Number(prev.Vacancy) >= 50) return prev;
        return { ...prev, Vacancy: Number(prev.Vacancy) + delta };
      });
      // Aided: keep the Eligible Vacant Post rows in sync with Vacancy count.
      if (isAidedSchls && delta > 0) {
        setVcntArray((prev) => [
          ...prev,
          { ifhrmsId: '', postGoKey: '', postGoUrl: '', _uid: newUid() },
        ]);
      }
    },
    [isAidedSchls]
  );

  // Aided: remove one vacant-post row (Angular handleAidedVacancyMinus).
  // Angular: always record the removal (even for rows with no IndexId), splice
  // the row out, and decrement the Vacancy count.
  // Note: keep setDeleteArray OUT of the setVcntArray updater — nesting a state
  // update inside another updater double-fires under React StrictMode and would
  // record the deletion twice.
  const removeAidedVacancy = useCallback(
    (index: number) => {
      const removed = vcntArray[index];
      setDeleteArray((d) => [
        ...d,
        { IsActive: '0', IndexId: removed?.IndexId },
      ]);
      setVcntArray((prev) => prev.filter((_, i) => i !== index));
      setUdiseDetails((prev: any) =>
        prev ? { ...prev, Vacancy: Number(prev.Vacancy) - 1 } : prev
      );
    },
    [vcntArray]
  );

  // Aided: rebuild the vacant-post rows when Vacancy is typed (Angular aidedSchoolAry).
  const rebuildVcntArray = useCallback((count: number) => {
    const arr: any[] = [];
    for (let i = 0; i < count; i++) {
      arr.push({ ifhrmsId: '', postGoKey: '', postGoUrl: '', _uid: newUid() });
    }
    setVcntArray(arr);
  }, []);

  const updateVcntRow = useCallback(
    (index: number, field: string, value: any) => {
      setVcntArray((prev) =>
        prev.map((r, i) => (i === index ? { ...r, [field]: value } : r))
      );
    },
    []
  );

  // Aided: minority radio reset (Angular handleMinorityRadio).
  const handleMinorityRadio = useCallback((value: string) => {
    setUdiseDetails((prev: any) =>
      prev ? { ...prev, minorityYn: value, minorityType: '' } : prev
    );
  }, []);

  // Aided: GO file upload (Angular onSelectFile).
  const onSelectFile = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>, index: number) => {
      const files = e.target.files;
      if (!files || !files[0]) return;
      const file = files[0];
      if (file.size > 10000000) {
        alertService.error(
          'File can`t be uploaded because the file size is above 10MB'
        );
        return;
      }
      const parts = file.name.split('.');
      const ext = parts[1];
      if (!['png', 'jpg', 'jpeg', 'pdf'].includes(ext)) {
        alertService.error(
          'Only JPEG or PDF or PNG or PDF format can be uploaded'
        );
        return;
      }
      try {
        const result: any = await triggerSignedUrl({
          bucketName: 'renewalapplicationemis',
          ext,
          fileName: parts[0],
          expiry: 300,
        }).unwrap();
        if (result?.url) {
          await uploadFile({ signedUrl: result.url, file }).unwrap();
          updateVcntRow(index, 'postGoKey', result.key);
          fetchGoUrl(result.key, index);
          alertService.success('File Uploaded Successfully');
        } else {
          alertService.error('Error in Uploading File please try again');
        }
      } catch {
        alertService.error('Error in Uploading File please try again');
      }
    },
    [triggerSignedUrl, uploadFile, updateVcntRow, fetchGoUrl]
  );

  const stepNeed = useCallback((delta: number) => {
    setUdiseDetails((prev: any) => {
      if (!prev) return prev;
      const next = Number(prev.Need) + delta;
      if (delta > 0 && Number(prev.Need) >= 50) return prev;
      return { ...prev, Need: next };
    });
  }, []);

  const setVacancyValue = useCallback(
    (value: string) => {
      const clean = value.replace(/\D/g, '');
      const num = clean === '' ? 0 : Number(clean);
      setUdiseDetails((prev: any) =>
        prev ? { ...prev, Vacancy: clean === '' ? '' : num } : prev
      );
      if (isAidedSchls) rebuildVcntArray(num);
    },
    [isAidedSchls, rebuildVcntArray]
  );

  const setNeedValue = useCallback((value: string) => {
    const clean = value.replace(/\D/g, '');
    setUdiseDetails((prev: any) =>
      prev ? { ...prev, Need: clean === '' ? '' : Number(clean) } : prev
    );
  }, []);

  // ── Final submit (Angular handleFinalSubmit) ────────────────────────────────
  const handleFinalSubmit = useCallback(async () => {
    if (!udiseDetails) return;
    const need = Number(udiseDetails.Need);
    const vac = Number(udiseDetails.Vacancy);
    if (!(need >= 0 && vac >= 0)) {
      alertService.error(
        'Vacancy and Need must be greater than or equal to zero.'
      );
      return;
    }

    setCheckboxDclrtn(false);

    if (isAidedSchls) {
      // DDO required (except DEE type 10/2).
      if (!(udiseDetails.ddoCode || (userType === '10' && userType1 === '2'))) {
        alertService.error('Please fill DDO Code');
        return;
      }
      // Build the vacant-post records with IFHRMS validation.
      const goRecords: any[] = [];
      let valid = true;
      if (!(userType === '10' && userType1 === '2')) {
        for (const v of vcntArray) {
          if (!v.ifhrmsId) {
            valid = false;
            alertService.error('Please fill all IFHRMS Id.');
            break;
          }
          if (v.ifhrmsId.length < 4 || v.ifhrmsId.length > 11) {
            valid = false;
            alertService.error('Invalid IFHRMS Id');
            break;
          }
          goRecords.push({
            IndexId: v.IndexId ? v.IndexId : '',
            VacId: '',
            IfhrmsId: v.ifhrmsId,
            DdoCode: udiseDetails.ddoCode,
            SanPostGoFile: v.postGoKey,
            IsActive: 1,
          });
        }
      }
      if (!valid) return;

      const req = {
        records: [
          {
            IndexId: udiseDetails.VacId ? udiseDetails.VacId : '',
            SchlKeyId: udiseDetails.schoolId,
            Panel: panel,
            Desg: designation,
            Sub: subject,
            Vac: vac,
            IfhrmsId: '',
            DdoCode: udiseDetails.ddoCode,
            SanPostGoFile: '',
            Flag: '1',
            minorityYn: udiseDetails.minorityYn,
            minorityType:
              udiseDetails.minorityYn === 'No'
                ? '0'
                : udiseDetails.minorityType,
          },
        ],
      };
      setPstVancyNdParams(req);
      setPostVancyGoAry(goRecords);
      setDeclarationModel(true);
      return;
    }

    // Normal / SCERT branch.
    const req = {
      records: [
        {
          IndexId: udiseDetails.VacId ? udiseDetails.VacId : '',
          SchlKeyId: udiseDetails.schoolId,
          Panel: panel,
          Desg: designation,
          Sub: subject,
          Vac: vac,
          Need: need,
        },
      ],
    };
    try {
      const res: any = await addVacancyNeed(req).unwrap();
      if (res?.dataStatus) {
        alertService.success(res.message);
        setIsDetailsShow(false);
        setIsListShow(true);
        handleSubmit();
      } else {
        alertService.error(res.message);
      }
    } catch {
      alertService.error('Something went wrong. Please try again');
    }
  }, [
    udiseDetails,
    panel,
    designation,
    subject,
    isAidedSchls,
    userType,
    userType1,
    vcntArray,
    addVacancyNeed,
    handleSubmit,
  ]);

  // ── Aided: declaration Yes → save vacancy + GO + approval (Angular handleVerified) ─
  const handleVerified = useCallback(async () => {
    if (!pstVancyNdParams) return;
    try {
      const resVac: any = await addVacancyNeed(pstVancyNdParams).unwrap();
      if (!resVac?.dataStatus) {
        alertService.error(resVac.message);
        return;
      }

      // DEE (type 10) — no GO/approval step.
      if (userType === '10') {
        setIsDetailsShow(false);
        setUdiseDetails(null);
        handleSubmit();
        setDeclarationModel(false);
        return;
      }

      // Post GO records (stamp the new VacId), merged with deletions.
      const goAry = postVancyGoAry.map((r) => ({
        ...r,
        VacId: resVac.IndxID,
      }));
      const params = { records: goAry.concat(deleteArray) };
      const resGo: any = await vcntGoPost(params).unwrap();
      if (!resGo?.dataStatus) return;

      // Approval forward (PA 30) / approve (CEO 9).
      if (
        userType === '30' ||
        (userType === '9' && isAidedSchls) ||
        (userType === '10' && isAidedSchls)
      ) {
        const vcntParams = {
          records: [
            {
              IndexId: resVac.IndxID,
              ApprovalSts: userType === '30' ? 1 : userType === '9' ? 2 : '',
            },
          ],
        };
        const resAprl: any = await vcntApprovalPost(vcntParams).unwrap();
        if (resAprl?.dataStatus) {
          alertService.success(
            userType === '30'
              ? 'Vacancy forwarded successfully.'
              : 'Vacancy approved successfully.'
          );
        }
      } else {
        alertService.success(resGo.message);
      }
      // Return to the list: hide the detail page and re-run the search.
      setIsDetailsShow(false);
      setUdiseDetails(null);
      handleSubmit();
      setDeclarationModel(false);
    } catch {
      alertService.error('Something went wrong. Please try again');
    }
  }, [
    pstVancyNdParams,
    postVancyGoAry,
    deleteArray,
    userType,
    isAidedSchls,
    addVacancyNeed,
    vcntGoPost,
    vcntApprovalPost,
    handleSubmit,
  ]);

  // ── Back to list (Angular handleBack) ───────────────────────────────────────
  const handleBack = useCallback(() => {
    setIsListShow(true);
    setIsDetailsShow(false);
    handleSubmit();
  }, [handleSubmit]);

  // ── Bulk upload (Angular excelTemplate / handleUploadFile / handleUpload) ────

  /** Download a sample .xlsx template (Angular excelTemplate). */
  const excelTemplate = useCallback(() => {
    const sample = [
      {
        UDISE: '',
        Vacancy: '',
        Need: '',
        District: '',
        Block: '',
        School: '',
        Category: '',
        Management: '',
        Designation: '',
        Subject: '',
        Panel: '',
      },
    ];
    const ws = XLSX.utils.json_to_sheet(sample);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Sample Template');
    XLSX.writeFile(wb, 'Sample Template.xlsx');
  }, []);

  /** Read the selected .xlsx into a rows-of-arrays structure (header:1). */
  const handleUploadFile = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files;
      if (!files || files.length !== 1) return;
      const reader = new FileReader();
      reader.onload = (ev: ProgressEvent<FileReader>) => {
        const bstr = ev.target?.result as string;
        const wb = XLSX.read(bstr, { type: 'binary' });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const aoa = XLSX.utils.sheet_to_json(ws, { header: 1 }) as any[];
        setUploadData(aoa);
      };
      reader.readAsBinaryString(files[0]);
    },
    []
  );

  /** Clear the selected bulk-upload file and its parsed rows. */
  const clearUploadFile = useCallback(() => {
    setUploadData([]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, []);

  /** Resolve school ids + IndexIds, then chunk-save (Angular getBulkSchId). */
  const getBulkSchId = useCallback(
    async (udiseList: string, requests: any[]) => {
      const req = {
        records: {
          UdiseCode: udiseList,
          desig: designation,
          panel,
          sub: subject ? subject : '',
        },
      };
      try {
        const res: any = await triggerBulkSchFromUdise(req).unwrap();
        if (!res?.dataStatus) {
          alertService.error(res.message);
          return;
        }
        const schList: any[] = res.result ?? [];

        // Block other-district UDISE for non-type-5.
        if (userType !== '5') {
          const other = schList.find(
            (item) => Number(item.district_id) !== districtId
          );
          if (other) {
            alertService.error(
              `Other district udise code(${other.udise_code}) should not be allowed`
            );
            return;
          }
        }

        // Attach SchlKeyId / IndexId to each request row.
        const resolved = requests.map((r) => {
          const found = schList.find(
            (item) => String(item.udise_code) === String(r.Udise)
          );
          return {
            ...r,
            SchlKeyId: found?.school_id,
            IndexId: found?.vacIndexId,
          };
        });

        // Chunk into 1000 and save each chunk.
        const chunkSize = 1000;
        let allOk = true;
        for (let i = 0; i < resolved.length; i += chunkSize) {
          const chunk = resolved.slice(i, i + chunkSize);
          try {
            const save: any = await addVacancyNeed({ records: chunk }).unwrap();
            if (!save?.dataStatus) {
              allOk = false;
              alertService.error(save.message);
            }
          } catch {
            allOk = false;
            alertService.error('Something went wrong. Please try again');
          }
        }

        if (allOk) {
          setActiveIndexId(0);
          setUploadData([]);
          if (fileInputRef.current) fileInputRef.current.value = '';
          alertService.success('Updated Successfully');
          handleSubmit();
        }
      } catch {
        alertService.error('Something went wrong. Please try again');
      }
    },
    [
      designation,
      panel,
      subject,
      userType,
      districtId,
      triggerBulkSchFromUdise,
      addVacancyNeed,
      handleSubmit,
    ]
  );

  /** Validate the uploaded rows and kick off the bulk save (Angular handleUpload). */
  const handleUpload = useCallback(() => {
    if (!(uploadData?.length > 0)) {
      alertService.error('File is empty. Please select correct file');
      return;
    }
    // First row is the header; map subsequent rows to objects by header key.
    const keys = Object.values(uploadData[0]) as string[];
    const rows: any[] = [];
    for (const raw of uploadData) {
      if (raw && raw.length !== 0) {
        const obj: any = {};
        keys.forEach((key, idx) => (obj[key] = raw[idx]));
        rows.push(obj);
      }
    }

    const requests: any[] = [];
    const udiseArr: string[] = [];
    let valid = true;
    // rows[0] is the header object; data starts at index 1.
    for (let i = 1; i < rows.length; i++) {
      const udise = rows[i].UDISE;
      const udiseStr = udise != null ? udise.toString() : '';
      if (!udiseStr) {
        valid = false;
        alertService.error('Udise code is empty. File should not be uploaded.');
        break;
      }
      if (udiseStr.length !== 11) {
        valid = false;
        alertService.error(`Invalid udise code (${udiseStr})`);
        break;
      }
      requests.push({
        IndexId: '',
        Udise: rows[i].UDISE,
        Panel: panel,
        Desg: designation,
        Sub: subject ? subject : '',
        Vac: rows[i].Vacancy,
        Need: rows[i].Need,
      });
      udiseArr.push(rows[i].UDISE);
    }

    if (valid) {
      getBulkSchId(udiseArr.toString(), requests);
    }
  }, [uploadData, panel, designation, subject, getBulkSchId]);

  // ── Minority label lookup (Angular minorityTypeList mapping) ────────────────
  const minorityLabel = useCallback(
    (value: any) => {
      const m = minorityTypeList.filter((ct) => str(ct.value) === str(value));
      return m.length > 0 ? m[0].label : '';
    },
    [minorityTypeList]
  );

  // ── Single-list export (Angular exportExcel 'excel' | 'pdf') ────────────────
  const exportList = useCallback(
    (stg: 'excel' | 'pdf') => {
      const rows = schoolList
        .map((r: any) => {
          const base: any = {
            UDISE: r.udise_code,
            Vacancy: r.Vacancy ? r.Vacancy : 0,
            Need: r.Need ? r.Need : 0,
            District: r.District,
            Block: r.Block,
            School: r.school,
            Category: r.Category,
            Management: r.Management,
            Designation: r.DesignationName,
            Subject: r.SubjectName,
            Ifhrms: r.ifhrmsId,
            DDOCode: r.ddoCode,
            MinorityYn: r.minorityYn,
            MinorityType:
              r.minorityYn === 'No' ? '' : minorityLabel(r.minorityType),
          };
          return base;
        })
        .filter((r: any) =>
          isAidedSchls ? r.Vacancy > 0 : r.Vacancy > 0 || r.Need > 0
        );

      if (rows.length === 0) {
        alertService.warning('Does not have vacancy in schools');
        return;
      }

      // Build the column set per mode (Angular removeKeyFromObjects branches).
      const columns: { key: string; label: string }[] = [];
      if (!isScert) columns.push({ key: 'UDISE', label: 'UDISE' });
      columns.push({ key: 'Vacancy', label: 'Vacancy' });
      if (!isAidedSchls) columns.push({ key: 'Need', label: 'Need' });
      columns.push(
        { key: 'District', label: 'District' },
        { key: 'Block', label: 'Block' }
      );
      if (!isScert) {
        columns.push(
          { key: 'School', label: 'School' },
          { key: 'Category', label: 'Category' },
          { key: 'Management', label: 'Management' }
        );
      }
      columns.push(
        { key: 'Designation', label: 'Designation' },
        { key: 'Subject', label: 'Subject' }
      );
      if (isAidedSchls) {
        columns.push(
          { key: 'Ifhrms', label: 'Ifhrms' },
          { key: 'DDOCode', label: 'DDOCode' },
          { key: 'MinorityYn', label: 'MinorityYn' },
          { key: 'MinorityType', label: 'MinorityType' }
        );
      }

      if (stg === 'excel') {
        exportToExcel({
          data: rows,
          columns: columns as ExcelColumn[],
          fileName: 'Counselling Vacancies',
        });
      } else {
        exportToPdf({
          data: rows,
          columns: columns as PdfColumn[],
          fileName: 'Counselling Vacancy',
          title: `Vacancies    Total Number of Vacancy - ${totalVacancy || 0}`,
        });
      }
    },
    [schoolList, isAidedSchls, isScert, totalVacancy, minorityLabel]
  );

  // ── Bulk vacancy export (Angular handleBulkExportVncy) ──────────────────────
  const handleBulkExportVncy = useCallback(
    async (stg: 'excel' | 'pdf') => {
      if (stg === 'excel') setBtnExcelLdg(true);
      else setBtnPdfLdg(true);
      try {
        const res: any = await triggerBulkVacancy().unwrap();
        if (!res?.dataStatus) {
          alertService.error(res.message);
          return;
        }
        // Filter to the logged-in district, map minority label, keep Vaccancy > 0.
        const list: any[] = (res.result ?? [])
          .filter((x: any) => str(x.District) === districtName)
          .map((row: any) => {
            const next = { ...row };
            if (next.minorityType) {
              next.minorityType = minorityLabel(next.minorityType);
            }
            if (next.minorityYn === 'No') next.minorityType = '';
            return next;
          })
          .filter((ft: any) => Number(ft.Vaccancy) > 0);

        if (list.length === 0) {
          alertService.warning('Does not have vacancy in schools');
          return;
        }

        const totalBulk = list.reduce(
          (acc: number, cur: any) => acc + Number(cur.Vaccancy || 0),
          0
        );

        // Columns follow the API row shape (Angular uses Object.keys of row 0).
        const columns = Object.keys(list[0]).map((k) => ({
          key: k,
          label: k,
        }));

        if (stg === 'excel') {
          exportToExcel({
            data: list,
            columns: columns as ExcelColumn[],
            fileName: 'Counselling Bulk Vacancies',
          });
        } else {
          exportToPdf({
            data: list,
            columns: columns as PdfColumn[],
            fileName: 'Bulk Vacancy',
            title: `Bulk Vacancy    Total Number of Vacancy - ${totalBulk}`,
          });
        }
      } catch {
        alertService.error('Something went wrong. Please try again');
      } finally {
        setBtnExcelLdg(false);
        setBtnPdfLdg(false);
      }
    },
    [triggerBulkVacancy, districtName, minorityLabel]
  );

  // ── Navigation ──────────────────────────────────────────────────────────────
  const handleCandidateURL = useCallback(
    () => navigate('/counselling/front-page'),
    [navigate]
  );

  const globalFilterFields = ['udise_code', 'school', 'Block'];

  return (
    <div className="main-body-content">
      {/* Breadcrumb */}
      {/* Search bar */}
      <div className="p-card p-3">
        <div className="row">
          <div className="col-md-3 my-3">
            <label className="inMandatory">
              <b>Designation</b>
            </label>
            <br />
            <Dropdown
              value={designation}
              options={staffList}
              optionLabel="label"
              optionValue="value"
              placeholder="Please Select designation"
              filter
              filterBy="label"
              showClear={!!designation}
              appendTo="self"
              className="w-100"
              onChange={(e) => handleDesignation(e.value ?? '')}
            />
          </div>
          <div className="col-md-3 my-3">
            <label className="inMandatory">
              <b>Panel</b>
            </label>
            <br />
            <Dropdown
              value={panel}
              options={panelList}
              optionLabel="label"
              optionValue="value"
              placeholder="Please select panel"
              showClear={!!panel}
              appendTo="self"
              className="w-100"
              onChange={(e) => {
                setPanel(e.value ?? '');
                hideList();
              }}
            />
          </div>
          {subjectList.length > 0 && (
            <div className="col-md-3 my-3">
              <label className="inMandatory">
                <b>Subject</b>
              </label>
              <br />
              <Dropdown
                value={subject}
                options={subjectList}
                optionLabel="label"
                optionValue="value"
                placeholder="Please select subject"
                showClear={!!subject}
                appendTo="self"
                className="w-100"
                onChange={(e) => {
                  setSubject(e.value ?? '');
                  hideList();
                }}
              />
            </div>
          )}
          <div className="col-md-1 mt-4">
            <Button
              type="button"
              label="Submit"
              className="p-button-primary mt-4"
              disabled={isSubmitDisabled}
              onClick={handleSubmit}
            />
          </div>

          {/* Bulk vacancy export (Angular handleBulkExportVncy) */}
          <div className="col-md-2 mt-4 text-end">
            <Button
              type="button"
              className="p-button-success mt-4"
              tooltip="Export Excel Bulk Vacancy"
              tooltipOptions={{ position: 'top' }}
              disabled={btnExcelLdg}
              icon={btnExcelLdg ? 'pi pi-spin pi-spinner' : 'pi pi-file-excel'}
              onClick={() => handleBulkExportVncy('excel')}
            />
            &nbsp;
            <Button
              type="button"
              className="p-button-danger mt-4"
              tooltip="Export PDF Bulk Vacancy"
              tooltipOptions={{ position: 'top' }}
              disabled={btnPdfLdg}
              icon={btnPdfLdg ? 'pi pi-spin pi-spinner' : 'pi pi-file-pdf'}
              onClick={() => handleBulkExportVncy('pdf')}
            />
          </div>
        </div>
      </div>
      <br />

      {/* List + bulk tabs */}
      {isListShow && (
        <div className="Counselling-Secondary">
          <TabView
            activeIndex={activeIndexId}
            onTabChange={(e) => setActiveIndexId(e.index)}
          >
            <TabPanel header="Vacancies Single Update">
              <DataTable
                value={schoolList}
                loading={loading}
                paginator
                rows={10}
                first={first}
                onPage={(e) => setFirst(e.first)}
                rowsPerPageOptions={[10, 25, 50]}
                rowHover
                responsiveLayout="scroll"
                emptyMessage="No Records Found"
                globalFilter={globalFilter}
                globalFilterFields={globalFilterFields}
                paginatorTemplate="CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
                currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries"
                paginatorRight={
                  <b>
                    Total Number of Vacancy : {totalVacancy ? totalVacancy : 0}
                  </b>
                }
                header={
                  <div className="row">
                    <div className="col-md-3 col-12">
                      <MultiSelect
                        value={selectedCols}
                        options={allColumns}
                        optionLabel="header"
                        placeholder="Choose Columns"
                        selectedItemsLabel="{0} columns selected"
                        onChange={(e) => setSelectedCols(e.value)}
                      />
                    </div>
                    <div className="col-md-3 col-12"></div>
                    <div className="col-md-6 col-12 text-end">
                      <InputText
                        type="text"
                        placeholder="Search.."
                        value={globalFilter}
                        onChange={(e) => setGlobalFilter(e.target.value)}
                      />
                      &nbsp; &nbsp;
                      <Button
                        type="button"
                        className="p-button-success"
                        label="Export As Excel"
                        disabled={schoolList.length === 0}
                        onClick={() => exportList('excel')}
                      />
                      &nbsp;
                      <Button
                        type="button"
                        className="p-button-danger"
                        label="Export As PDF"
                        disabled={schoolList.length === 0}
                        onClick={() => exportList('pdf')}
                      />
                    </div>
                  </div>
                }
              >
                <Column
                  header="S.No"
                  body={(_row: any, options) => first + options.rowIndex + 1}
                />
                {orderedCols.map((col) => (
                  <Column
                    key={col.field}
                    field={col.field}
                    header={col.header}
                    sortable
                  />
                ))}
                <Column
                  header="Action"
                  body={(row: any) => (
                    <Button
                      type="button"
                      className={
                        row.btnText === 'View'
                          ? 'p-button-success'
                          : 'p-button-primary'
                      }
                      label={row.btnText}
                      onClick={() => handleEdit(row)}
                    />
                  )}
                />
              </DataTable>
            </TabPanel>

            {!isAidedSchls && (
              <TabPanel header="Vacancies Bulk Update">
                <p>
                  <b>Only for updating Vacancy in Schools, not Offices.</b>
                </p>
                <div className="row">
                  <div className="col">
                    <b>Step 1:</b> Please click the sample template and enter
                    data in the downloaded excel sheet. <br />
                    <b>Step 2:</b> Please upload the downloaded excel sheet by
                    clicking Upload button.
                  </div>
                </div>
                <br />
                <div className="row">
                  <div className="col">
                    <label>Vacancies Bulk Upload</label>
                  </div>
                </div>
                <div className="row">
                  <div className="col-md-4 text-end">
                    <div className="input-group">
                      <input
                        ref={fileInputRef}
                        type="file"
                        name="files"
                        className="form-control"
                        accept=".xlsx"
                        onChange={handleUploadFile}
                      />
                      {uploadData.length > 0 && (
                        <span
                          className="input-group-text"
                          role="button"
                          tabIndex={0}
                          title="Clear file"
                          onClick={clearUploadFile}
                          style={{ cursor: 'pointer', color: '#dc3545' }}
                        >
                          <i className="pi pi-times" />
                        </span>
                      )}
                    </div>
                    <span
                      className="excl"
                      style={{
                        color: 'blue',
                        textDecoration: 'underline',
                        cursor: 'pointer',
                      }}
                      onClick={excelTemplate}
                    >
                      Sample Template
                    </span>
                  </div>
                  <div className="col-md-7">
                    <Button
                      type="button"
                      icon="pi pi-upload"
                      label="Upload"
                      onClick={handleUpload}
                    />
                  </div>
                </div>
              </TabPanel>
            )}
          </TabView>
        </div>
      )}

      {/* ── Detail form (Phase 2) ──────────────────────────────────────── */}
      {isDetailsShow && udiseDetails && (
        <>
          <div className="row">
            <div className="col-12 text-end">
              <Button
                type="button"
                icon="pi pi-arrow-left"
                label="Back"
                className="p-button-danger"
                onClick={handleBack}
              />
            </div>
          </div>

          <div className="row mt-4">
            <div className="col">
              <div className="Counselling-Secondary">
                <div className="p-datatable p-component p-datatable-gridlines">
                  <div className="p-datatable-header">School Details</div>
                  <table className="p-datatable-table w-100">
                    <tbody className="p-datatable-tbody">
                      <tr>
                        <td>
                          <b>District</b>
                        </td>
                        <td>{udiseDetails.District}</td>
                      </tr>
                      <tr>
                        <td>
                          <b>Block</b>
                        </td>
                        <td>{udiseDetails.Block}</td>
                      </tr>
                      {!isScert && (
                        <tr>
                          <td>
                            <b>UDISE Code</b>
                          </td>
                          <td>{udiseDetails.udise_code}</td>
                        </tr>
                      )}
                      <tr>
                        <td>
                          <b>School Name</b>
                        </td>
                        <td>{udiseDetails.school}</td>
                      </tr>
                      {!isScert && (
                        <tr>
                          <td>
                            <b>Management</b>
                          </td>
                          <td>{udiseDetails.Management}</td>
                        </tr>
                      )}
                      {!isScert && (
                        <tr>
                          <td>
                            <b>Category</b>
                          </td>
                          <td>{udiseDetails.Category}</td>
                        </tr>
                      )}
                      <tr>
                        <td>
                          <b>Designation</b>
                        </td>
                        <td>{udiseDetails.DesignationName}</td>
                      </tr>

                      {/* Aided: DDO code (except DEE type 10) */}
                      {isAidedSchls && userType !== '10' && (
                        <tr>
                          <td>
                            <b>DDO CODE</b>
                          </td>
                          <td>
                            <div className="col-4">
                              <InputText
                                value={udiseDetails.ddoCode ?? ''}
                                keyfilter="int"
                                maxLength={8}
                                className="form-control"
                                placeholder="Enter DDO Code"
                                onChange={(e) =>
                                  setUdiseDetails((prev: any) =>
                                    prev
                                      ? { ...prev, ddoCode: e.target.value }
                                      : prev
                                  )
                                }
                              />
                            </div>
                          </td>
                        </tr>
                      )}

                      {/* Aided: Minority Yn */}
                      {isAidedSchls && (
                        <tr>
                          <td>
                            <b>Minority</b>
                          </td>
                          <td>
                            <div className="col">
                              <RadioButton
                                inputId="minY"
                                name="minorityYn"
                                value="Yes"
                                checked={udiseDetails.minorityYn === 'Yes'}
                                onChange={() => handleMinorityRadio('Yes')}
                              />
                              &nbsp;<label htmlFor="minY">Yes</label>
                              &nbsp;&nbsp;&nbsp;
                              <RadioButton
                                inputId="minN"
                                name="minorityYn"
                                value="No"
                                checked={udiseDetails.minorityYn === 'No'}
                                onChange={() => handleMinorityRadio('No')}
                              />
                              &nbsp;<label htmlFor="minN">No</label>
                            </div>
                          </td>
                        </tr>
                      )}

                      {/* Aided: Minority Type */}
                      {isAidedSchls && udiseDetails.minorityYn === 'Yes' && (
                        <tr>
                          <td>
                            <b>Minority Type</b>
                          </td>
                          <td>
                            <div className="col-4">
                              <Dropdown
                                value={udiseDetails.minorityType}
                                options={minorityTypeList}
                                optionLabel="label"
                                optionValue="value"
                                placeholder="Please select minority"
                                showClear={!!udiseDetails.minorityType}
                                appendTo="self"
                                className="w-100"
                                onChange={(e) =>
                                  setUdiseDetails((prev: any) =>
                                    prev
                                      ? { ...prev, minorityType: e.value ?? '' }
                                      : prev
                                  )
                                }
                              />
                            </div>
                          </td>
                        </tr>
                      )}

                      {/* Vacancy stepper */}
                      <tr>
                        <td>
                          <b>Vacancy</b>
                        </td>
                        <td>
                          <div className="row align-items-center">
                            {/* Minus only for normal mode or DEE (type 10) */}
                            {(!isAidedSchls || userType === '10') && (
                              <div className="col-1 text-end">
                                <Button
                                  type="button"
                                  className="p-button-danger"
                                  icon="pi pi-minus"
                                  onClick={() => stepVacancy(-1)}
                                />
                              </div>
                            )}
                            <div className="col-3">
                              {/* Aided: readonly once the Vacancy count is above
                                  zero; editable again when it drops to zero. */}
                              <InputText
                                value={String(udiseDetails.Vacancy)}
                                keyfilter="int"
                                maxLength={2}
                                className="form-control"
                                placeholder="Vacancy Count.."
                                readOnly={
                                  isAidedSchls &&
                                  Number(udiseDetails.Vacancy) > 0
                                }
                                onChange={(e) =>
                                  setVacancyValue(e.target.value)
                                }
                              />
                            </div>
                            <div className="col-1">
                              <Button
                                type="button"
                                className="p-button-success"
                                icon="pi pi-plus"
                                onClick={() => stepVacancy(1)}
                              />
                            </div>
                          </div>
                        </td>
                      </tr>

                      {/* Need stepper (normal mode only) */}
                      {!isAidedSchls && (
                        <tr>
                          <td>
                            <b>Need</b>
                          </td>
                          <td>
                            <div className="row align-items-center">
                              <div className="col-1 text-end">
                                <Button
                                  type="button"
                                  className="p-button-danger"
                                  icon="pi pi-minus"
                                  onClick={() => stepNeed(-1)}
                                />
                              </div>
                              <div className="col-3">
                                <InputText
                                  value={String(udiseDetails.Need)}
                                  keyfilter="int"
                                  maxLength={2}
                                  className="form-control"
                                  placeholder="Need Count.."
                                  onChange={(e) => setNeedValue(e.target.value)}
                                />
                              </div>
                              <div className="col-1">
                                <Button
                                  type="button"
                                  className="p-button-success"
                                  icon="pi pi-plus"
                                  onClick={() => stepNeed(1)}
                                />
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>

          {/* Aided: Eligible Vacant Post table (IFHRMS + GO upload per row) */}
          {isAidedSchls && userType !== '10' && vcntArray.length > 0 && (
            <div className="row mt-4">
              <div className="col">
                <div className="Counselling-Secondary">
                  <div className="p-datatable p-component p-datatable-gridlines">
                    <b>Eligible Vacant Post</b>
                    <table className="p-datatable-table w-100">
                      <thead className="p-datatable-thead">
                        <tr>
                          <th>S.No</th>
                          <th>IFHRMS Id</th>
                          <th>Sanctioned Post GO</th>
                          <th>View</th>
                          <th>Action</th>
                        </tr>
                      </thead>
                      <tbody className="p-datatable-tbody">
                        {vcntArray.map((item, i) => (
                          <tr key={item._uid ?? i}>
                            <td>{i + 1}</td>
                            <td>
                              <InputText
                                value={item.ifhrmsId ?? ''}
                                keyfilter="int"
                                maxLength={11}
                                className="form-control"
                                placeholder="Enter IFHRMS Id"
                                onChange={(e) =>
                                  updateVcntRow(i, 'ifhrmsId', e.target.value)
                                }
                              />
                            </td>
                            <td>
                              <input
                                type="file"
                                className="form-control"
                                accept=".png,.jpg,.jpeg,.pdf"
                                onChange={(e) => onSelectFile(e, i)}
                              />
                            </td>
                            <td>
                              {item.postGoKey ? (
                                <a
                                  href={item.postGoUrl || undefined}
                                  target="_blank"
                                  rel="noreferrer"
                                >
                                  <Button
                                    type="button"
                                    className="p-button-info"
                                    icon="pi pi-eye"
                                    disabled={!item.postGoUrl}
                                  />
                                </a>
                              ) : (
                                '-'
                              )}
                            </td>
                            <td>
                              <Button
                                type="button"
                                className="p-button-danger"
                                icon="pi pi-minus"
                                onClick={() => removeAidedVacancy(i)}
                              />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="row">
            <div className="col text-end">
              {udiseDetails.btnText === 'Edit' && (
                <Button
                  type="button"
                  label={sbmtFwdText}
                  className="p-button-primary mt-4"
                  onClick={handleFinalSubmit}
                />
              )}
            </div>
          </div>
        </>
      )}

      {/* Aided: declaration dialog (Angular declarationModel) */}
      <Dialog
        header="Verification"
        visible={declarationModel}
        style={{ width: '45vw' }}
        headerClassName="dialog-header-secondary"
        onHide={() => setDeclarationModel(false)}
      >
        <div className="row mt-4">
          <div className="col-12 d-flex">
            <Checkbox
              inputId="dclrtn"
              checked={checkboxDclrtn}
              onChange={(e) => setCheckboxDclrtn(!!e.checked)}
            />
            &nbsp;
            <label htmlFor="dclrtn">
              The above vacancy details have been verified and certified that
              all these vacancies are eligible to be filled up as per the staff
              fixation order issued by me for the year 2026-2027.
            </label>
          </div>
        </div>
        <div className="row mt-4">
          <div className="col-12 text-end">
            {checkboxDclrtn ? (
              <>
                <Button
                  type="button"
                  label="Yes"
                  className="p-button-success"
                  onClick={handleVerified}
                />
                &nbsp; &nbsp;
                <Button
                  type="button"
                  label="No"
                  className="p-button-danger"
                  onClick={() => setDeclarationModel(false)}
                />
              </>
            ) : (
              ''
            )}
          </div>
        </div>
      </Dialog>
    </div>
  );
};

export default VacancyUpdate;

/* eslint-enable @typescript-eslint/no-explicit-any */
